import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, Grid, TextField, Button, IconButton, Tooltip, MenuItem } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { addAttributesByEntityId } from "../../../../api's/DataModelApi";

const dataTypeOptions = ['VARCHAR', 'INTEGER', 'TIMESTAMP', 'BOOLEAN', 'DATE', 'FLOAT', 'TEXT'];
const boolOptions = [true, false];

const AddAttributeModal = ({ open, onClose, onSuccess, entityId, existingAttributes = [] }) => {
  const { enqueueSnackbar } = useSnackbar();
  const INITIAL_ATTRIBUTES = [
    {
      name: '',
      description: '',
      data_type: 'VARCHAR',
      required_col: true,
      unique_col: false,
      custom_col: true,
    },
  ];
  const [attributes, setAttributes] = useState(INITIAL_ATTRIBUTES);

  const handleAttrChange = (index, field, value) => {
    const updated = [...attributes];
    updated[index][field] = value;
    setAttributes(updated);
  };

  const addNewRow = () => {
    setAttributes([
      ...attributes,
      {
        name: '',
        description: '',
        data_type: 'VARCHAR',
        required_col: true,
        unique_col: false,
        custom_col: true,
      },
    ]);
  };

  const removeRow = (index) => {
    const updated = [...attributes];
    updated.splice(index, 1);
    setAttributes(updated);
  };

  const handleSubmit = async () => {
    const trimmedAttrs = attributes.map((a) => ({
      ...a,
      name: a.name.trim(),
      description: a.description.trim(),
    }));

    // Check for empty name or description
    if (trimmedAttrs.some((a) => !a.name || !a.description)) {
      enqueueSnackbar('Each attribute must have a name and description.', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return;
    }

    // Combine and check duplicates (case-insensitive)
    const allNames = [
      ...existingAttributes.map((a) => a.name.toLowerCase()),
      ...trimmedAttrs.map((a) => a.name.toLowerCase()),
    ];

    const nameCounts = {};
    allNames.forEach((name) => {
      nameCounts[name] = (nameCounts[name] || 0) + 1;
    });

    const duplicateNames = Object.keys(nameCounts).filter((name) => nameCounts[name] > 1);

    if (duplicateNames.length > 0) {
      enqueueSnackbar(`Attribute  ${duplicateNames.join(', ')} already exists.`, {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      enqueueSnackbar(`Please delete it first before adding again.`, {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return;
    }

    try {
      const payload = { attributes: trimmedAttrs };
      const res = await addAttributesByEntityId(entityId, payload);
      enqueueSnackbar('Attributes added successfully!', {
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      if (onSuccess) onSuccess(trimmedAttrs); // update UI
      onClose();
      setAttributes([
        {
          name: '',
          description: '',
          data_type: 'VARCHAR',
          required_col: true,
          unique_col: false,
          custom_col: true,
        },
      ]);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to add attributes. Please try again.';
      enqueueSnackbar(msg, {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      console.error('❌ Error adding attributes:', err);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          p: 4,
          width: 850,
          maxHeight: '70vh',
          // overflowY: 'auto',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Add Attributes
        </Typography>

        <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
          {attributes.map((attr, index) => (
            <Box key={index} sx={{ p: 1, background: '#fafafa', borderRadius: 1, mb: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Attribute Name"
                    value={attr.name}
                    onChange={(e) => handleAttrChange(index, 'name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={attr.description}
                    onChange={(e) => handleAttrChange(index, 'description', e.target.value)}
                  />
                </Grid>
                <Grid item xs={2}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Data Type"
                    value={attr.data_type}
                    onChange={(e) => handleAttrChange(index, 'data_type', e.target.value)}
                  >
                    {dataTypeOptions.map((dt) => (
                      <MenuItem key={dt} value={dt}>
                        {dt}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={1.5}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Required"
                    value={attr.required_col}
                    onChange={(e) => handleAttrChange(index, 'required_col', e.target.value === 'true')}
                  >
                    {boolOptions.map((b) => (
                      <MenuItem key={b.toString()} value={b.toString()}>
                        {b.toString()}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={1.5}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Unique"
                    value={attr.unique_col}
                    onChange={(e) => handleAttrChange(index, 'unique_col', e.target.value === 'true')}
                  >
                    {boolOptions.map((b) => (
                      <MenuItem key={b.toString()} value={b.toString()}>
                        {b.toString()}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={1}>
                  <Tooltip title="Remove Attribute">
                    <IconButton color="error" onClick={() => removeRow(index)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>

        <Button startIcon={<Add />} sx={{ mb: 2, textTransform: 'none' }} onClick={addNewRow}>
          Add More Attribute
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={() => {
            setAttributes(INITIAL_ATTRIBUTES);
            onClose();
          }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddAttributeModal;
